import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { PlusButton } from "@/components/design/PlusButton";
import { Select } from "@/components/design/Select";
import {
  DeleteQuestionnaireDocument,
  QuestionnaireDetailDocument,
  QuestionnairesListDocument,
  SaveQuestionnaireDocument,
} from "@/graphql/generated/operations";
// codegen emits these as nominal `enum`s for the mutation input; the draft keeps
// plain strings (the enums' runtime values) and casts only at the save boundary.
import type { CreativityElement, QuestionnaireStatus } from "@/graphql/generated/operations";
import type { FieldOption } from "@/lib/field-util";
import { ELEMENT_CODES } from "@/lib/elements";
import { POWER_BY_ABBR } from "@/lib/power-library";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";

type QStatus = "ACTIVE" | "INACTIVE";

// Admin authoring for grow questionnaires: list on the left, editor on the
// right. saveQuestionnaire upserts the whole graph (questionnaire
// + questions + weighted answer choices); null ids create. Embedded in the
// Systemeinstellungen → Fragebögen section (gated by write:any:questionnaire);
// genesis enforces the real permission server-side.

const inputCls =
  "w-full rounded-md bg-page px-3 py-2 text-[13px] leading-relaxed outline-none ring-1 ring-ink/10 transition focus:ring-accent/40";

// element enum → localized power name, for the element selects.
function useElOptions(): FieldOption[] {
  const intl = useIntl();
  return Object.entries(ELEMENT_CODES)
    .map(([el, abbr]) => {
      const nameId = POWER_BY_ABBR[abbr]?.nameId;
      return { value: el, label: nameId ? intl.formatMessage({ id: nameId }) : el };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

const STATUS_OPTION_IDS: { value: QStatus; labelId: string }[] = [
  { value: "ACTIVE", labelId: "admin.statusActive" },
  { value: "INACTIVE", labelId: "admin.statusInactive" },
];

type DraftChoice = { qnrAnswerChoiceId?: string; answer: string; weight: number };
type DraftQuestion = {
  qnrQuestionId?: string;
  question: string;
  creativityElement: string;
  status: string;
  choices: DraftChoice[];
};
type Draft = {
  questionnaireId?: string;
  name: string;
  instructions: string;
  creativityElement: string | null;
  status: QStatus;
  questions: DraftQuestion[];
};

function emptyQuestion(element: string): DraftQuestion {
  return {
    question: "",
    creativityElement: element || Object.keys(ELEMENT_CODES)[0] || "",
    status: "ACTIVE",
    choices: [
      { answer: "", weight: 0 },
      { answer: "", weight: 100 },
    ],
  };
}

export function QuestionnaireManager() {
  const intl = useIntl();
  const { data, refetch } = useQuery(QuestionnairesListDocument, {
    variables: { input: {} },
    fetchPolicy: "cache-and-network",
  });
  const list = data?.questionnaires ?? [];
  // null = nothing selected, "new" = creating, otherwise a questionnaireId.
  const [selected, setSelected] = useState<string | "new" | null>(null);
  // The list panel collapses leftward to a thin rail to give the editor room.
  const [listOpen, setListOpen] = useState(true);

  return (
    <div
      className={`grid grid-cols-1 gap-6 @lg:items-start ${
        listOpen
          ? "@lg:grid-cols-[minmax(240px,320px)_minmax(0,1fr)]"
          : "@lg:grid-cols-[auto_minmax(0,1fr)]"
      }`}
    >
      {listOpen ? (
        <aside className="box bg-surface p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {intl.formatMessage({ id: "admin.qTitle", defaultMessage: "Questionnaires" })}
              <span className="ml-1 text-[11px] text-ink/45">({list.length})</span>
            </span>
            <Button
              variant="icon"
              size="sm"
              onClick={() => setListOpen(false)}
              aria-label={intl.formatMessage({ id: "admin.collapseList", defaultMessage: "Collapse list" })}
            >
              <span className="text-sm leading-none">‹</span>
            </Button>
          </div>
          <Button
            variant="primary"
            onClick={() => setSelected("new")}
            className="mb-3 w-full"
          >
            {intl.formatMessage({ id: "admin.newQuestionnaire", defaultMessage: "+ New questionnaire" })}
          </Button>
          {list.length ? (
            <ul className="space-y-1">
              {list.map((q) => (
                <li key={q.questionnaireId}>
                  <MenuItem
                    active={selected === q.questionnaireId}
                    onClick={() => setSelected(q.questionnaireId)}
                    trailing={<StatusDot status={String(q.status)} />}
                  >
                    {q.name}
                  </MenuItem>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-1 py-2 text-[12px] text-ink/45">{intl.formatMessage({ id: "admin.noQuestionnaires", defaultMessage: "No questionnaires yet." })}</div>
          )}
        </aside>
      ) : (
        <aside>
          <div className="box bg-surface flex w-10 items-center justify-center py-2 @lg:sticky @lg:top-6">
            <Button
              variant="icon"
              size="sm"
              onClick={() => setListOpen(true)}
              aria-label={intl.formatMessage({ id: "admin.expandList", defaultMessage: "Show questionnaires" })}
              title={intl.formatMessage({ id: "admin.qTitle", defaultMessage: "Questionnaires" })}
            >
              <span className="text-sm leading-none">›</span>
            </Button>
          </div>
        </aside>
      )}

        {selected ? (
          <Editor
            key={selected}
            questionnaireId={selected === "new" ? null : selected}
            onSaved={(id) => {
              void refetch();
              setSelected(id);
            }}
            onDeleted={() => {
              void refetch();
              setSelected(null);
            }}
            onCancel={() => setSelected(null)}
          />
        ) : (
          <div className="box flex min-h-[200px] items-center justify-center bg-surface p-6 text-[13px] text-ink/45">
            {intl.formatMessage({ id: "admin.selectOrCreate", defaultMessage: "Select a questionnaire on the left or create a new one." })}
          </div>
        )}
    </div>
  );
}

function Editor({
  questionnaireId,
  onSaved,
  onDeleted,
  onCancel,
}: {
  questionnaireId: string | null;
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const listEntry = useQuery(QuestionnairesListDocument, {
    variables: { input: {} },
    fetchPolicy: "cache-first",
  }).data?.questionnaires.find((q) => q.questionnaireId === questionnaireId);

  const { data, loading } = useQuery(QuestionnaireDetailDocument, {
    variables: { questionnaireId: questionnaireId ?? "" },
    skip: !questionnaireId,
    fetchPolicy: "cache-and-network",
  });

  if (questionnaireId && loading && !data) {
    return <div className="box bg-surface p-6 text-[13px] text-ink/45">{intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })}</div>;
  }

  const initial: Draft = questionnaireId
    ? {
        questionnaireId,
        name: listEntry?.name ?? "",
        instructions: listEntry?.instructions ?? "",
        creativityElement: listEntry?.creativityElement ? String(listEntry.creativityElement) : null,
        status: String(listEntry?.status) === "INACTIVE" ? "INACTIVE" : "ACTIVE",
        questions: [...(data?.questionnaireQuestions ?? [])]
          .sort((a, b) => a.position - b.position)
          .map((q) => ({
            qnrQuestionId: q.qnrQuestionId,
            question: q.question,
            creativityElement: String(q.creativityElement),
            status: String(q.status),
            choices: [...q.answerChoices]
              .sort((a, b) => a.position - b.position)
              .map((c) => ({
                qnrAnswerChoiceId: c.qnrAnswerChoiceId,
                answer: c.answer,
                weight: c.weight,
              })),
          })),
      }
    : {
        name: "",
        instructions: "",
        creativityElement: null,
        status: "ACTIVE",
        questions: [emptyQuestion("")],
      };

  return (
    <EditorForm
      key={questionnaireId ?? "new"}
      initial={initial}
      onSaved={onSaved}
      onDeleted={onDeleted}
      onCancel={onCancel}
    />
  );
}

function EditorForm({
  initial,
  onSaved,
  onDeleted,
  onCancel,
}: {
  initial: Draft;
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const EL_OPTIONS = useElOptions();
  const [draft, setDraft] = useState<Draft>(initial);
  const STATUS_OPTIONS: FieldOption[] = STATUS_OPTION_IDS.map((o) => ({
    value: o.value,
    label: intl.formatMessage({ id: o.labelId }),
  }));
  const [save, { loading, error }] = useMutation(SaveQuestionnaireDocument);
  const [del, { loading: deleting, error: deleteError }] = useMutation(DeleteQuestionnaireDocument);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function remove() {
    if (!draft.questionnaireId || deleting) return;
    try {
      await del({ variables: { questionnaireId: draft.questionnaireId } });
      onDeleted();
    } catch {
      logApplicationFailure("admin.delete-questionnaire");
    }
  }

  const valid =
    draft.name.trim().length > 0 &&
    draft.questions.length > 0 &&
    draft.questions.every(
      (q) =>
        q.question.trim().length > 0 &&
        q.creativityElement &&
        q.choices.length > 0 &&
        q.choices.every((c) => c.answer.trim().length > 0),
    );

  function patch(p: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...p }));
  }
  function patchQuestion(i: number, p: Partial<DraftQuestion>) {
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, qi) => (qi === i ? { ...q, ...p } : q)),
    }));
  }

  async function submit() {
    if (!valid || loading) return;
    try {
      const res = await save({
        variables: {
          input: {
            questionnaire: {
              questionnaireId: draft.questionnaireId,
              name: draft.name.trim(),
              status: draft.status as unknown as QuestionnaireStatus,
              creativityElement: draft.creativityElement
                ? (draft.creativityElement as unknown as CreativityElement)
                : null,
              instructions: draft.instructions.trim() || null,
            },
            questions: draft.questions.map((q, qi) => ({
              qnrQuestionId: q.qnrQuestionId,
              question: q.question.trim(),
              creativityElement: q.creativityElement as unknown as CreativityElement,
              position: qi,
              status: q.status as unknown as QuestionnaireStatus,
              answerChoices: q.choices.map((c, ci) => ({
                qnrAnswerChoiceId: c.qnrAnswerChoiceId,
                answer: c.answer.trim(),
                weight: c.weight,
                position: ci,
                status: "ACTIVE" as unknown as QuestionnaireStatus,
              })),
            })),
          },
        },
      });
      const id = res.data?.saveQuestionnaire.questionnaire.questionnaireId;
      if (id) onSaved(id);
    } catch {
      logApplicationFailure("admin.save-questionnaire");
    }
  }

  return (
    <div className="box bg-surface p-5 @sm:p-6">
      <div className="grid grid-cols-1 gap-4 @sm:grid-cols-[1fr_auto]">
        <label className="block">
          <span className="field-label text-ink/55">{intl.formatMessage({ id: "admin.nameLabel", defaultMessage: "Name" })}</span>
          <input
            className={`mt-1 ${inputCls}`}
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder={intl.formatMessage({ id: "admin.namePlaceholder", defaultMessage: "e.g. Empathy check" })}
          />
        </label>
        <label className="block @sm:w-40">
          <span className="field-label text-ink/55">{intl.formatMessage({ id: "admin.statusLabel", defaultMessage: "Status" })}</span>
          <Select
            className="mt-1"
            value={draft.status}
            onChange={(v) => patch({ status: v as QStatus })}
            options={STATUS_OPTIONS}
          />
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 @sm:grid-cols-[1fr_auto]">
        <label className="block">
          <span className="field-label text-ink/55">{intl.formatMessage({ id: "admin.instructionsLabel", defaultMessage: "Instructions (optional)" })}</span>
          <textarea
            className={`mt-1 ${inputCls} min-h-[60px] resize-y`}
            value={draft.instructions}
            onChange={(e) => patch({ instructions: e.target.value })}
            placeholder={intl.formatMessage({ id: "admin.instructionsPlaceholder", defaultMessage: "Hint for the person filling it out" })}
          />
        </label>
        <label className="block @sm:w-56">
          <span className="field-label text-ink/55">{intl.formatMessage({ id: "admin.defaultPowerLabel", defaultMessage: "Default power (optional)" })}</span>
          <Select
            className="mt-1"
            value={draft.creativityElement}
            onChange={(v) => patch({ creativityElement: v })}
            options={EL_OPTIONS}
            placeholder={intl.formatMessage({ id: "admin.nonePlaceholder", defaultMessage: "None" })}
          />
        </label>
      </div>

      <div className="mt-6 space-y-4">
        {draft.questions.map((q, qi) => (
          <QuestionCard
            key={qi}
            index={qi}
            q={q}
            canRemove={draft.questions.length > 1}
            onChange={(p) => patchQuestion(qi, p)}
            onRemove={() =>
              patch({ questions: draft.questions.filter((_, x) => x !== qi) })
            }
          />
        ))}
        <PlusButton
          onClick={() =>
            patch({ questions: [...draft.questions, emptyQuestion(draft.creativityElement ?? "")] })
          }
          label={intl.formatMessage({ id: "admin.addQuestion", defaultMessage: "Add question" })}
        />
      </div>

      {error ? (
        <div className="mt-4 text-[12px] text-red-600">{userErrorMessage(intl, error)}</div>
      ) : null}
      {deleteError ? (
        <div className="mt-4 text-[12px] text-red-600">{userErrorMessage(intl, deleteError)}</div>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-ink/5 pt-4">
        <Button
          variant="primary"
          onClick={() => void submit()}
          disabled={!valid || loading}
        >
          {loading
            ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving…" })
            : intl.formatMessage({ id: "common.save", defaultMessage: "Save" })}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
        </Button>
        {!valid ? (
          <span className="text-[11px] text-ink/45">
            {intl.formatMessage({ id: "admin.validationHint", defaultMessage: "Name, plus text + power per question and text per answer required." })}
          </span>
        ) : null}
        {draft.questionnaireId ? (
          confirmingDelete ? (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-ink/55">{intl.formatMessage({ id: "admin.confirmDeleteQ", defaultMessage: "Delete questionnaire?" })}</span>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => void remove()}
                disabled={deleting}
              >
                {deleting ? "…" : intl.formatMessage({ id: "admin.yesDelete", defaultMessage: "Yes, delete" })}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
              >
                {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmingDelete(true)}
              className="ml-auto"
            >
              {intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
            </Button>
          )
        ) : null}
      </div>
    </div>
  );
}

function QuestionCard({
  index,
  q,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number;
  q: DraftQuestion;
  canRemove: boolean;
  onChange: (p: Partial<DraftQuestion>) => void;
  onRemove: () => void;
}) {
  const intl = useIntl();
  const EL_OPTIONS = useElOptions();
  function patchChoice(ci: number, p: Partial<DraftChoice>) {
    onChange({ choices: q.choices.map((c, x) => (x === ci ? { ...c, ...p } : c)) });
  }
  return (
    <div className="box-flat bg-page/40 p-4">
      <div className="mb-3 flex items-start gap-3">
        <span className="mt-2 shrink-0 text-[11px] tabular-nums text-ink/40">{index + 1}.</span>
        <input
          className={inputCls}
          value={q.question}
          onChange={(e) => onChange({ question: e.target.value })}
          placeholder={intl.formatMessage({ id: "admin.questionPlaceholder", defaultMessage: "Question / statement" })}
        />
        <div className="w-44 shrink-0">
          <Select
            value={q.creativityElement}
            onChange={(v) => onChange({ creativityElement: v })}
            options={EL_OPTIONS}
            placeholder={intl.formatMessage({ id: "admin.selectPower", defaultMessage: "Select power" })}
          />
        </div>
        {canRemove ? (
          <Button
            variant="icon"
            size="sm"
            onClick={onRemove}
            aria-label={intl.formatMessage({ id: "admin.removeQuestion", defaultMessage: "Remove question" })}
            className="mt-1 shrink-0"
          >
            ✕
          </Button>
        ) : null}
      </div>

      <div className="space-y-1.5 pl-7">
        {q.choices.map((c, ci) => (
          <div key={ci} className="flex items-center gap-2">
            <input
              className={`${inputCls} flex-1`}
              value={c.answer}
              onChange={(e) => patchChoice(ci, { answer: e.target.value })}
              placeholder={intl.formatMessage({ id: "admin.answerPlaceholder", defaultMessage: "Answer option" })}
            />
            <label className="flex shrink-0 items-center gap-1.5">
              <span className="field-label text-ink/45">{intl.formatMessage({ id: "admin.weightLabel", defaultMessage: "Weight" })}</span>
              <input
                type="number"
                className={`${inputCls} w-20`}
                value={c.weight}
                onChange={(e) => patchChoice(ci, { weight: Number(e.target.value) || 0 })}
              />
            </label>
            {q.choices.length > 1 ? (
              <Button
                variant="icon"
                size="sm"
                onClick={() => onChange({ choices: q.choices.filter((_, x) => x !== ci) })}
                aria-label={intl.formatMessage({ id: "admin.removeAnswer", defaultMessage: "Remove answer" })}
                className="shrink-0"
              >
                ✕
              </Button>
            ) : null}
          </div>
        ))}
        <PlusButton
          onClick={() => onChange({ choices: [...q.choices, { answer: "", weight: 0 }] })}
          className="mt-1"
          label={intl.formatMessage({ id: "admin.addAnswer", defaultMessage: "Answer" })}
        />
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === "ACTIVE" ? "bg-emerald-500" : status === "INACTIVE" ? "bg-ink/30" : "bg-red-400";
  return <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${color}`} aria-label={status} />;
}
